import { useState, useEffect } from "react";
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  Search,
  Filter,
  ChevronDown,
  Calendar,
  User,
  FileText,
  TrendingDown,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Status badge colors
const STATUS_STYLES = {
  completed: { bg: "bg-green-500/20", text: "text-green-400", icon: CheckCircle, label: "Completed" },
  pending: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: Clock, label: "Pending" },
  failed: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle, label: "Failed" },
  manual_required: { bg: "bg-orange-500/20", text: "text-orange-400", icon: AlertTriangle, label: "Manual Required" }
};

// Reason labels
const REASON_LABELS = {
  client_request: "Client Request",
  stylist_unavailable: "Stylist Unavailable",
  service_issue: "Service Issue",
  double_booking: "Double Booking",
  emergency: "Emergency",
  dissatisfaction: "Client Dissatisfaction",
  price_adjustment: "Price Adjustment",
  other: "Other"
};

const RefundsDashboard = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refundsData, setRefundsData] = useState(null);
  const [timeRange, setTimeRange] = useState("30d");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/appointments/refunds/dashboard?time_range=${timeRange}&status_filter=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefundsData(response.data);
    } catch (error) {
      console.error("Error fetching refunds:", error);
      toast.error("Failed to load refunds data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchRefunds();
    }
  }, [token, timeRange, statusFilter]);

  const filteredRefunds = refundsData?.refunds?.filter(refund => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      refund.client_name?.toLowerCase().includes(search) ||
      refund.client_email?.toLowerCase().includes(search) ||
      refund.stylist_name?.toLowerCase().includes(search) ||
      refund.refund_id?.toLowerCase().includes(search) ||
      refund.reason?.toLowerCase().includes(search)
    );
  }) || [];

  const exportCSV = () => {
    if (!filteredRefunds.length) {
      toast.error("No refunds to export");
      return;
    }

    const headers = ["Refund ID", "Date", "Client", "Stylist", "Amount", "Original Amount", "Reason", "Status"];
    const rows = filteredRefunds.map(r => [
      r.refund_id,
      new Date(r.created_at).toLocaleDateString(),
      r.client_name || r.client_email,
      r.stylist_name,
      `$${r.refund_amount?.toFixed(2)}`,
      `$${r.original_amount?.toFixed(2)}`,
      REASON_LABELS[r.reason_template] || r.reason_template || "N/A",
      r.status
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refunds_${timeRange}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Refunds exported to CSV");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const stats = refundsData?.stats || {};

  return (
    <div className="space-y-6" data-testid="refunds-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Refunds Dashboard</h2>
          <p className="text-stone-400 mt-1">
            {user?.role === "owner" ? "Track all refunds across your salon" : "View and track your refunds"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRefunds}
            className="border-stone-700 text-stone-300 hover:bg-stone-800"
            data-testid="refresh-refunds-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            className="border-stone-700 text-stone-300 hover:bg-stone-800"
            data-testid="export-refunds-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">${stats.total_refunded?.toLocaleString() || "0"}</p>
              <p className="text-xs text-stone-500">Total Refunded</p>
            </div>
          </div>
        </div>
        
        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_count || 0}</p>
              <p className="text-xs text-stone-500">Total Refunds</p>
            </div>
          </div>
        </div>

        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.completed_count || 0}</p>
              <p className="text-xs text-stone-500">Completed</p>
            </div>
          </div>
        </div>

        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.pending_count || 0}</p>
              <p className="text-xs text-stone-500">Pending</p>
            </div>
          </div>
        </div>

        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.manual_required_count || 0}</p>
              <p className="text-xs text-stone-500">Manual Required</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input
              type="text"
              placeholder="Search by client, stylist, or refund ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              data-testid="refunds-search-input"
            />
          </div>

          {/* Time Range Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-stone-500" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              data-testid="time-range-filter"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-stone-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              data-testid="status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="manual_required">Manual Required</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reasons Breakdown (for owners) */}
      {user?.role === "owner" && refundsData?.reasons_breakdown && Object.keys(refundsData.reasons_breakdown).length > 0 && (
        <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-800">
          <h3 className="text-lg font-semibold text-white mb-4">Refund Reasons Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(refundsData.reasons_breakdown).map(([reason, data]) => (
              <div key={reason} className="bg-stone-800/50 rounded-lg p-3">
                <p className="text-sm font-medium text-white">{REASON_LABELS[reason] || reason}</p>
                <p className="text-lg font-bold text-amber-400">{data.count} refunds</p>
                <p className="text-xs text-stone-500">${data.total?.toFixed(2)} total</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refunds List */}
      <div className="bg-stone-900/50 rounded-xl border border-stone-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Client</th>
                {user?.role === "owner" && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Stylist</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider">ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-800">
              {filteredRefunds.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === "owner" ? 7 : 6} className="px-4 py-8 text-center text-stone-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No refunds found for the selected filters</p>
                  </td>
                </tr>
              ) : (
                filteredRefunds.map((refund) => {
                  const statusStyle = STATUS_STYLES[refund.status] || STATUS_STYLES.pending;
                  const StatusIcon = statusStyle.icon;
                  
                  return (
                    <tr 
                      key={refund.refund_id} 
                      className="hover:bg-stone-800/30 transition-colors"
                      data-testid={`refund-row-${refund.refund_id}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {new Date(refund.created_at).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-stone-500">
                          {new Date(refund.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center">
                            <User className="w-4 h-4 text-stone-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">
                              {refund.client_name || "Guest"}
                            </div>
                            <div className="text-xs text-stone-500">
                              {refund.client_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      {user?.role === "owner" && (
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-white">{refund.stylist_name}</div>
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-red-400">
                          -${refund.refund_amount?.toFixed(2)}
                        </div>
                        <div className="text-xs text-stone-500">
                          of ${refund.original_amount?.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-white">
                          {REASON_LABELS[refund.reason_template] || refund.reason_template || "N/A"}
                        </div>
                        {refund.notes && (
                          <div className="text-xs text-stone-500 truncate max-w-[150px]" title={refund.notes}>
                            {refund.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-xs text-stone-500 font-mono">
                          {refund.refund_id?.slice(0, 12)}...
                        </div>
                        {refund.stripe_refund_id && (
                          <div className="text-xs text-blue-400">
                            Stripe: {refund.stripe_refund_id.slice(0, 8)}...
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="text-center text-sm text-stone-500">
        <p>
          Refunds are processed automatically for Stripe payments. 
          For other payment methods, manual processing may be required.
        </p>
      </div>
    </div>
  );
};

export default RefundsDashboard;
